'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { useParams, useRouter } from 'next/navigation'

import {
  SearchParameterKeysTypeEnum,
  SearchParameterTypeEnum,
} from '@lib/enums/search.enum'
import { useMedusaSearchParametersStore } from '@lib/store/medusa-search/medusa-search-parameters.store'
import { useTranslations } from 'next-intl'
import type { SearchParameter } from 'types/search.type'

import ArrowBack from '../../public/arrow_back.svg'
import Cancel from '../../public/cancel.svg'
import { DateRangePickerView } from './DateRangePickerView'
import { DualRangeSlider } from './DualRangeSlider'
import { GPSSearchInput } from './GPSSearchInput'

type Props = {
  onClose: () => void
  onApply: (filters: SearchParameter[]) => void
  resetRef: React.RefObject<any>
}

export default function FilterPopup({ onClose, onApply, resetRef }: Props) {
  const {
    searchParameters,
    currentUserSearchParameters,
    setCurrentUserSearchParameter,
    clearCurrentUserSearchParameters,
    fetchSearchParameters,
    removeCurrentUserSearchParameter,
  } = useMedusaSearchParametersStore()

  const [searchResultsCount, setSearchResultsCount] = useState(0)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const gpsInputRef = useRef<any>(null)
  const [isDateMode, setIsDateMode] = useState(false)
  const { countryCode } = useParams() as { countryCode: string }
  const t = useTranslations('SearchPage')
  const router = useRouter()

  useEffect(() => {
    initializeSearchParameters()
  }, [])

  useEffect(() => {
    debounceSearchCount(currentUserSearchParameters)
  }, [currentUserSearchParameters])

  const initializeSearchParameters = async () => {
    await fetchSearchParameters(countryCode)
    applyUserSelectionToStore()
  }

  const debounceSearchCount = (params: SearchParameter[]) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchSearchCount(params)
    }, 500)
  }

  const fetchSearchCount = async (params: SearchParameter[]) => {
    try {
      const res = await fetch('/api/search/count', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locale: countryCode, searchParameters: params }),
      })

      const json = await res.json()
      setSearchResultsCount(json?.products ?? 0)
    } catch (err) {
      console.error('Erreur lors du comptage des résultats de recherche', err)
    }
  }

  const handleSwitchChange = (parameterKey: string, valueKey: string) => {
    const selectedValues = getSelectedValuesForKey(
      parameterKey,
      currentUserSearchParameters
    )
    const isSelected = selectedValues.includes(valueKey)
    const param = {
      [parameterKey]: {
        ...searchParameters[parameterKey],
        values: searchParameters[parameterKey].values.filter(
          (v) => v.key === valueKey
        ),
      },
    }
    setCurrentUserSearchParameter(param, valueKey, !isSelected)
  }

  const handleChipToggle = (parameterKey: string, valueKey: string) => {
    const selected = getSelectedValuesForKey(
      parameterKey,
      currentUserSearchParameters
    ).includes(valueKey)
    const param = { [parameterKey]: searchParameters[parameterKey] }
    setCurrentUserSearchParameter(param, valueKey, !selected)
  }

  const onReset = () => {
    clearCurrentUserSearchParameters()
    gpsInputRef.current?.clearSelection()
    initializeSearchParameters()
    router.replace('/search')
  }

  if (resetRef) {
    resetRef.current.addEventListener('click', () => {
      onReset()
    })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-3xl flex-col rounded-3xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {isDateMode ? (
          <>
            <PopupHeader
              onClose={onClose}
              title={t('chooseDatesRange')}
              showBackButton
              onReturn={() => setIsDateMode(false)}
            />

            <div className="mt-4">
              <DateRangePickerView />
            </div>
            <PopupFooter
              onReset={() => {
                const { searchParameters, clearSearchDateRange } =
                  useMedusaSearchParametersStore.getState()
                const dateKey = Object.keys(searchParameters).find(
                  (key) =>
                    searchParameters[key].type === SearchParameterTypeEnum.DATE
                )
                if (!dateKey) return

                // Vider les valeurs dans searchParameters
                const updatedDateParam = {
                  ...searchParameters[dateKey],
                  values: [],
                }

                // Mise à jour complète du store pour searchParameters et currentUserSearchParameters
                useMedusaSearchParametersStore.setState((prev) => ({
                  searchParameters: {
                    ...prev.searchParameters,
                    [dateKey]: updatedDateParam,
                  },
                  currentUserSearchParameters:
                    prev.currentUserSearchParameters.filter(
                      (param) => Object.keys(param)[0] !== dateKey
                    ),
                }))

                clearSearchDateRange()
              }}
              onApply={() => {
                const { searchParameters, searchDateRange } =
                  useMedusaSearchParametersStore.getState()
                const dateKey = Object.keys(searchParameters).find(
                  (key) =>
                    searchParameters[key].type === SearchParameterTypeEnum.DATE
                )

                if (!dateKey) return

                // Si aucune date n’est sélectionnée, ne fait rien
                if (
                  !searchDateRange ||
                  !searchDateRange.startDate ||
                  !searchDateRange.endDate
                )
                  return

                // Applique les dates sélectionnées dans l’état utilisateur
                const valueKey = `${searchDateRange.startDate};${searchDateRange.endDate}`

                setCurrentUserSearchParameter(
                  {
                    [dateKey]: {
                      ...searchParameters[dateKey],
                      values: [
                        {
                          key: valueKey,
                          label: `${formatDate(searchDateRange.startDate)} au ${formatDate(
                            searchDateRange.endDate
                          )}`,
                          selected: true,
                        },
                      ],
                    },
                  },
                  valueKey,
                  true
                )

                // Ferme le mode date
                setIsDateMode(false)
              }}
              buttonTitle={t('validate')}
            />
          </>
        ) : (
          <>
            <PopupHeader onClose={onClose} title={t('filters')} />
            <div className="flex-1 space-y-6 overflow-y-auto px-10 py-6">
              {Object.keys(searchParameters)
                .filter(
                  (key) =>
                    !searchParameters[key].hide &&
                    searchParameters[key].type !== SearchParameterTypeEnum.TEXT
                )
                .map((key, index, array) => (
                  <div
                    key={key}
                    className={`pb-6 ${index !== array.length - 1 ? 'border-b border-gray-200' : ''}`}
                  >
                    {renderParameter(
                      key,
                      searchParameters,
                      currentUserSearchParameters,
                      removeCurrentUserSearchParameter,
                      setCurrentUserSearchParameter,
                      handleChipToggle,
                      handleSwitchChange,
                      setIsDateMode,
                      gpsInputRef,
                    )}
                  </div>
                ))}
            </div>
            <PopupFooter
              onReset={onReset}
              onApply={() => onApply(currentUserSearchParameters)}
              resultCount={searchResultsCount}
              buttonTitle={`${t('see')} ${searchResultsCount} ${t('results')}`}
            />
          </>
        )}
      </div>
    </div>
  )
}

// 🧩 Composants UI extraits pour clarté
const PopupHeader = ({
  title,
  showBackButton = false,
  onClose,
  onReturn,
}: {
  title: string
  showBackButton?: boolean
  onClose: () => void
  onReturn?: () => void
}) => (
  <div className="relative flex items-center justify-center border-b px-10 py-6">
    <h2 className="text-2xl font-bold">{title}</h2>
    <div
      onClick={onClose}
      className="absolute right-8 top-1/2 -translate-y-1/2 cursor-pointer rounded-full text-gray-500 hover:text-black"
    >
      <Image src={Cancel} alt="close" />
    </div>
    {showBackButton && (
      <div
        onClick={onReturn}
        className="absolute left-8 top-1/2 -translate-y-1/2 cursor-pointer rounded-full text-gray-500 hover:text-black"
      >
        <Image src={ArrowBack} alt="close" />
      </div>
    )}
  </div>
)

const PopupFooter = ({
  onReset,
  onApply,
  resultCount,
  buttonTitle,
}: {
  onReset: () => void
  onApply: () => void
  buttonTitle: string
  resultCount?: number
}) => {
  const t = useTranslations('SearchPage')
  const isDisabled = resultCount === 0

  return (
    <div className="flex items-center justify-between border-t px-10 py-6">
      <button onClick={onReset} className="font-medium">
        {t('reset')}
      </button>
      <button
        onClick={onApply}
        disabled={isDisabled}
        className={`rounded-md px-6 py-3 text-white ${
          isDisabled ? 'cursor-not-allowed bg-gray-400' : 'bg-green'
        }`}
      >
        {buttonTitle}
      </button>
    </div>
  )
}

export const SwitchParameter = ({
  label = undefined,
  values,
  onChange,
}: {
  label?: string
  values: any[]
  onChange: (key: string) => void
}) => (
  <div>
    {label && (
      <label className="mb-3 block text-[19px] font-medium">{label}</label>
    )}
    <div className="flex w-fit gap-2 rounded-full border-2 border-black p-1">
      {values.map((val) => (
        <button
          key={val.key}
          onClick={() => onChange(val.key)}
          className={`rounded-full px-6 py-2 small:px-10 small:py-2 ${val.selected ? 'bg-black text-white' : 'text-black'}`}
        >
          {val.label}
        </button>
      ))}
    </div>
  </div>
)

export const ArrayParameter = ({
  label,
  values,
  onToggle,
}: {
  label: string
  values: any[]
  onToggle: (key: string) => void
}) => (
  <div>
    <label className="mb-1 block text-sm font-medium">{label}</label>
    <div className="flex flex-wrap gap-2">
      {values.map((val) => (
        <button
          key={val.key}
          onClick={() => onToggle(val.key)}
          className={`rounded-full border bg-white px-2 text-sm ${val.selected ? 'border-black text-black' : 'border-gray-300 text-gray-400'}`}
        >
          {val.label}
        </button>
      ))}
    </div>
  </div>
)

const DateParameter = ({
  label,
  onClick,
}: {
  label: string
  onClick: () => void
}) => {
  const { currentUserSearchParameters } = useMedusaSearchParametersStore()
  const t = useTranslations('SearchPage')

  const dateKey = currentUserSearchParameters.find(
    (param) => Object.values(param)?.[0]?.type === SearchParameterTypeEnum.DATE
  )

  const selectedDateLabel = dateKey
    ? Object.values(dateKey)?.[0]?.values?.[0]?.label
    : null

  return (
    <div>
      <label className="mb-3 block text-[19px] font-medium">{label}</label>
      <button
        onClick={onClick}
        className={`rounded-full border-2 border-black px-8 py-2 ${
          selectedDateLabel
            ? 'border-black text-black'
            : 'border-gray-300 text-gray-400'
        }`}
      >
        {selectedDateLabel || t('chooseDateRange')}
      </button>
    </div>
  )
}

const PlaceholderParameter = ({ label }: { label: string }) => (
  <div>
    <p>On est dans {label}</p>
  </div>
)

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return `${String(date.getDate()).padStart(2, '0')}/${String(
    date.getMonth() + 1
  ).padStart(2, '0')}/${String(date.getFullYear()).slice(-2)}`
}

export const renderParameter = (
  parameterKey: string,
  searchParameters: SearchParameter,
  currentUserSearchParameters: SearchParameter[],
  removeCurrentUserSearchParameter: (key: string) => void,
  setCurrentUserSearchParameter: (
    parameter: SearchParameter,
    value: any,
    selected: boolean
  ) => void,
  onArrayChange?: (parameterKey: string, valueKey: string) => void,
  onSwitchChange?: (parameterKey: string, valueKey: string) => void,
  onDateClick?: (bool: boolean) => void,
  gpsInputRef?,
) => {
  const param = searchParameters[parameterKey]
  if (param.hide) return null
  const selectedKeys = getSelectedValuesForKey(
    parameterKey,
    currentUserSearchParameters
  )

  switch (param.type) {
    case SearchParameterTypeEnum.SWITCH:
      return (
        <SwitchParameter
          key={parameterKey}
          label={param.label}
          values={param.values.map((v) => ({
            ...v,
            selected: selectedKeys.includes(v.key),
          }))}
          onChange={(key) => onSwitchChange(parameterKey, key)}
        />
      )
    case SearchParameterTypeEnum.ARRAY:
      return (
        <ArrayParameter
          key={parameterKey}
          label={param.label}
          values={param.values.map((v) => ({
            ...v,
            selected: selectedKeys.includes(v.key),
          }))}
          onToggle={(key) => onArrayChange(parameterKey, key)}
        />
      )

    case SearchParameterTypeEnum.GPS_DISTANCE:
      const selectedGps = getSelectedValueGps(
        SearchParameterKeysTypeEnum.WHERE_GPS,
        currentUserSearchParameters
      )
      return (
        <div key={parameterKey}>
          <label className="mb-3 block text-[19px] font-medium">
            {param.label}
          </label>
          <GPSSearchInput
            ref={gpsInputRef}
            selectedValue={selectedGps || null}
            onSelection={(location) => {
              removeCurrentUserSearchParameter(
                SearchParameterKeysTypeEnum.WHERE_GPS
              )

              const formattedParam = {
                [SearchParameterKeysTypeEnum.WHERE_GPS]: {
                  ...param,
                  values: [
                    {
                      label: `${location.display_name || 'Lieu sélectionné'}`,
                      key: `${location.lat};${location.lon}`,
                    },
                  ],
                },
              }

              setCurrentUserSearchParameter(
                formattedParam,
                formattedParam[SearchParameterKeysTypeEnum.WHERE_GPS].values[0]
                  .key,
                true
              )
            }}
          />
        </div>
      )

    case SearchParameterTypeEnum.DATE:
      return (
        <DateParameter label={param.label} onClick={() => onDateClick(true)} />
      )

              let timer;
    case SearchParameterTypeEnum.RANGE:
      return (
        <div className="flex flex-col gap-4">
          <p className="font-bold">{param.label}</p>
          <DualRangeSlider
            min={param.values.find((el) => el.label == 'min').key}
            max={param.values.find((el) => el.label == 'max').key}
            refreshParams={(min, max) => {
              removeCurrentUserSearchParameter('price_range')
              
              const formattedParam = {
                ["price_range"]: {
                  ...param,
                  values: [
                    {
                      label: `${param.label}`,
                      key: `${min};${max}`,
                    },
                  ],
                },
              }

              setCurrentUserSearchParameter(
                formattedParam,
                formattedParam["price_range"].values[0]
                  .key,
                true
              )
            }}
          />
        </div>
      )

    default:
      return <PlaceholderParameter key={parameterKey} label={param.label} />
  }
}

export const getSelectedValuesForKey = (
  parameterKey: string,
  currentUserSearchParameters: SearchParameter[]
): string[] => {
  const found = currentUserSearchParameters.find(
    (p) => Object.keys(p)[0] === parameterKey
  )
  return (
    found?.[parameterKey]?.values
      ?.filter((v) => v.selected)
      .map((v) => v.key) || []
  )
}

const getSelectedValueGps = (
  parameterKey: string,
  currentUserSearchParameters: SearchParameter[]
) => {
  try {
    if (!parameterKey || !Array.isArray(currentUserSearchParameters))
      return null

    const found = currentUserSearchParameters.find(
      (param) =>
        param &&
        typeof param === 'object' &&
        Object.prototype.hasOwnProperty.call(param, parameterKey) &&
        param[parameterKey]?.values &&
        Array.isArray(param[parameterKey].values) &&
        param[parameterKey].values.length > 0
    )

    const value = found?.[parameterKey]?.values?.[0]
    return value ?? null
  } catch (err) {
    console.warn('getSelectedValueGps error:', err)
    return null
  }
}

export const applyUserSelectionToStore = () => {
  const { searchParameters, currentUserSearchParameters } =
    useMedusaSearchParametersStore.getState()

  for (const param of currentUserSearchParameters) {
    const key = Object.keys(param)[0]
    if (!key || !searchParameters[key]) continue
    const selectedKeys = param[key]?.values?.map((v: any) => v.key) || []
    const spValues = searchParameters[key]?.values || []

    searchParameters[key].values = spValues.map((v: any) => ({
      ...v,
      selected: selectedKeys.includes(v.key),
    }))
  }

  useMedusaSearchParametersStore.setState({
    searchParameters: { ...searchParameters },
  })
}
